// packages/ui/react/AuthorityModal.jsx — RUN RB. THE AUTHORITY READS WITHOUT LEAVING THE SCREEN.
//
// ══ 🔴 RUN CT — PROMOTED OUT OF `apps/main/client/src/screens/Clark/answer/`. ════════════════
//
// RUN CE Block 4 stopped here and was right to: Research could not import this file, because it was
// not in the research image. That was a PACKAGING failure, not a design one. Adam's ruling was to
// PROMOTE it, not to port a copy into steel — "a copy is the lookalike I did not want, and it would
// drift on the first edit to either."
//
// ⭐ SO THE PARITY IS NOT A RESEMBLANCE, IT IS AN IDENTITY. Clark and Research mount THIS file. There
// is no second stylesheet, no second fetch, no second skeleton and no second close button that could
// diverge — the only way the two screens can differ is if one of them passes a different `authority`.
// Everything below is RUN RB's, unchanged except the four import specifiers at the top.
//
// ⚠ IT KEEPS SAYING "CLARK" THROUGHOUT THE NOTES BELOW, AND THAT IS LEFT ALONE ON PURPOSE. Those
// paragraphs record WHY each decision was made and the reasons were Clark's reasons; rewriting them
// to say "Clark and Research" would falsify the history without making the component any more
// shared. `data-testid` values keep their `clark-` prefix for exactly the same reason — renaming
// them would break RUN RB's specs to no benefit.
//
// ════════════════════════════════════════════════════════════════════════════════════
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 WHAT THIS REPLACES. Clicking an authority in Clark's rail NAVIGATED to Research, and the
// Clark thread was gone — the answer the lawyer was reading, the question they asked, the rest of
// the authorities. Adam's ruling, 2026-08-24: *"we should bring up the text as a modal that shows
// the content instead. That way, you never leave the Clark screen."*
//
// Built 1:1 against `apps/main/ops/design/clark-authority-modal-v1.html` for layout, type scale
// and structure. That file governs those; it does not govern colour ROLES, and one deviation from
// it is deliberate and measured — see THE `--cc-page` DEVIATION in authorityModal.css.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 THE SHARED `Modal` IS IMPORTED, NOT FORKED, AND THAT IS THE POINT OF THE IMPORT.
//
// `./Modal.jsx` is described in its own header as the *"shared blurred-blue modal primitive"*.
// It owns the portal to <body>, the Esc key, the backdrop click, and — the reason a second shell
// would have been a real defect — THE BLUR. Its `.cc-modal-overlay` is `backdrop-filter: blur(6px)`.
//
// ⭐ AND THE MOCKUP'S "STAND-IN" WAS ALREADY THE REAL VALUE. The design file flags its `--blur:6px`
// as a placeholder to be replaced by the app's actual modal backdrop (rule 5), and the app's actual
// modal backdrop is `blur(6px)`. So importing the primitive changes no pixel of the backdrop and
// guarantees there is exactly ONE blur value in the product. Nothing here sets `backdrop-filter`.
//
// 🔴 HOW THE 860px DOCUMENT SHELL FITS A 28rem PRIMITIVE WITHOUT EDITING IT. `Modal` takes no
// `className`, so this file cannot put a hook on `.cc-modal-card`. The geometry the design calls
// for is therefore applied from authorityModal.css with `:has(.cc-authmodal)` — our stylesheet
// reaching the primitive's element, rather than the primitive growing a variant for us. Nothing in
// chrome/ is touched, and a card that does not contain an authority modal is unaffected.
//
// `title`/`icon` are deliberately NOT passed: supplying either renders the primitive's centred navy
// header, and this modal's head is a document head — serif name, reporter cite, court, date.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 THE HEADER NEEDS NO FETCH — RULE 4, AND IT IS THE WHOLE LATENCY DESIGN.
//
// Adam's report was that bringing up case text *"takes FOREVER to the point it seems broken."* The
// fix is not a faster query, it is a different order: the modal opens FROM a rail card that already
// holds the authority's identity, so the head paints from that at 0 ms and only the BODY is
// fetched. A modal that paints instantly and fills in reads fast even when the fetch is slow; one
// that waits before painting anything reads broken.
//
// ⚠️ ONE PREMISE OF THE BRIEF WAS HALF TRUE — AND IT IS NOW TRUE. As written, this note said the
// rail holds NAME and CITE only. RUN TG added `year` to `railPayload.js` and RUN UC added `court`
// (envelope → snapshot → rail), so the head now paints kind · name · cite · court · year at 0 ms
// and only the DATE waits on the body. The original text, for the record: "It does not hold court
// or year: `railPayload.js` maps ten fields off the v2 authority row and `court`/`year` are not
// among them" (RUN PJ measured the same absence from the other side —
// 0 `.cc-auth__sig` elements in a 6-card rail, because no `citationCount` is passed either). So the
// head paints kind · name · cite at 0 ms and court · date arrive with the body, which the `.meta`
// row is built to absorb without reflowing the title. `railPayload.js` is RUN PN's file and is not
// touched here; the one-line spec is in the report.
//
// ⚠️ NO CENTRED SPINNER. At eight seconds a spinner reads as failure. Skeleton lines in the shape
// of the content read as arriving. The mockup draws both and this renders the skeleton.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 NO TREATMENT. NONE. No pill, no dot, no `--cc-goodlaw-*`, no "no known negative treatment".
// Adam's ruling, 2026-08-24. There is no branch in this file that can draw one, and `pillFlag` is
// deliberately not imported — leaving the import here would make re-adding one a one-line accident,
// which is the same reasoning AuthorityCard.jsx records for dropping `TreatmentPill`.
//
// ⭐ BUT THE STATUTE PROVENANCE LINE IS NOT TREATMENT AND IT SURVIVES. *"Text as of 2026-05-04
// (119-100) — verbatim from the U.S. Code. Verify no later amendment."* That is the cardinal rule
// made visible — statute text comes from govinfo/GPO and eCFR, never from a model — and it is the
// single most important string on this surface. It is rendered from `federal.currency`, a stored
// column pair (`as_of_date`, `source_release`), not from anything inferred. A sweep that
// pattern-matches on status-shaped strings will try to delete it. IT MUST NOT.
import { useCallback, useEffect, useRef, useState } from "react";
// 🔴 RUN CT — THE PRIMITIVE CAME WITH IT, AND THE CONSUMER COUNT IS WHY.
// `Modal.jsx` had exactly TWO importers in apps/main (this file and DocCheck), so promoting it
// alongside this modal was a smaller change than leaving it behind would have been — and leaving it
// behind had only one other outcome: a second portal-and-Esc implementation in packages/ui, which is
// how two modals start behaving differently under the keyboard and nobody notices for a month.
// ONE modal chrome, ONE blur value, ONE Esc handler, ONE focus trap.
import Modal from "./Modal.jsx";
// ⭐ RUN CT — the link builder moved to `packages/ui/authorityLink.mjs` for the same reason this
// file did. Clark's `answer/authorityLink.mjs` re-exports it, so its nine call sites are untouched.
import { researchHref } from "../authorityLink.mjs";
// 🔴 RUN MA — the SHARED filing control, not a Clark-local copy. Research mounts the same
// component from the same file; see its header for why one implementation and not two.
// ⚠ RELATIVE NOW, NOT `@citeclerk/ui/FileToMatter`: this file IS packages/ui. A self-referencing
// bare specifier resolves under Node's exports map but is a bundler-resolution coin toss, and the
// sibling components here (AuthorityCard, ReverificationModal) all use relative paths.
import FileToMatter from "./FileToMatter.jsx";
import "./FileToMatter.css";
import "./authorityModal.css";

/* ══ 🔴 THE ROUTES, AND THE ANSWER TO "CAN CLARK REACH TEXT TODAY" IS YES ═══════════════════════
   Established before anything was built, because the brief's STEP 0 makes a new steel route a STOP
   — RUN RE is inside steel right now. Neither route needed building:

   · `apps/steel/server/index.js:53-57` already strips a `/research` prefix off `/api/*`, with the
     comment *"the citeclerk.com path router forwards /research/api/* to this app"*. So a
     same-origin `/research/api/…` from Clark reaches steel's API.
   · `ops/router/Caddyfile` proxies `/research /research/*` to the research app and everything else
     to the citations app — which is also why this must be `/research/api/…` and not `/api/…`:
     a bare `/api/case/:id` goes to apps/main, whose routes/ directory has no case route at all.
   · Same origin means the Clerk session cookie rides along, which steel's `/api` auth requires.

   The two routes:
     CASE     GET /research/api/case/:id/reading   → { caseName, court, dateFiled, citation,
                                                       opinion: { text, truncated, preview } }
     STATUTE  GET /research/api/statutes/search?q= → { federal: { cite, heading, text,
                                                       currency: { asOfDate, sourceRelease } } }

   🔴 `/reading` IS THE FAST HALF, DELIBERATELY. `/api/case/:id` is the combined route whose
   good-law block RUN AH measured at 931 ms of a 1,021 ms open, and whose `corpus_currency`
   aggregate RUN NC measured at 24,722 ms of a 25,042 ms open. `/reading` runs neither. This surface
   draws nothing from either, so it waits for neither. */
const CASE_ROUTE = (id) => `/research/api/case/${encodeURIComponent(String(id))}/reading`;
const STATUTE_ROUTE = (cite) => `/research/api/statutes/search?q=${encodeURIComponent(String(cite))}`;

const isStatute = (a) => a?.kind === "statute" || a?.type === "Statute";

/* ── the statute body's (a)(1)(A) structure ──────────────────────────────────────────────────
   The corpus stores a section as plain text; the hanging indents that carry its structure are a
   RENDERING of the markers already in that text, never an edit to it. Every character of the
   source survives — the marker is re-emitted with the line it introduces — so this cannot drop a
   subsection the way a parser that rewrote the text could.

   🔴 THE MARKER IS ON ITS OWN LINE, AND THIS WAS WRITTEN AGAINST THE REAL BYTES RATHER THAN THE
   MOCKUP. The design file draws `(a) It shall be unlawful—` as one line, so the first cut of this
   function matched a leading `\(x\)` and kept the rest. MEASURED on the live federal payload for
   18 U.S.C. § 922 (55,939 chars, 793 lines) the U.S. Code arrives as:

       (a)⏎ It shall be unlawful—⏎ (1)⏎ for any person—⏎ (A)⏎ except a licensed importer…

   — **278 of its 793 lines are a bare marker and nothing else.** Against that text the first cut
   produced 278 orphan paragraphs reading "(a)", "(1)", "(A)" with every clause flattened to one
   level and the whole (a)(1)(A) structure gone. The mockup governs LAYOUT, and its layout is
   right; it does not govern the shape of the corpus, and only fetching it shows that.

   ⚠️ A LINE WHOSE MARKER WE DO NOT RECOGNISE IS STILL SHOWN, at the level of the clause it
   follows. On a surface that renders verbatim law an unparsed line must never be dropped — the
   failure this repo has paid for twice is truncation that presents itself as completeness. */
const MARKER = /^\(([0-9A-Za-z]{1,4})\)$/;      // a line that is ONLY a marker
const INLINE = /^(\(([0-9A-Za-z]{1,4})\))\s+(?=\S)/;  // a marker leading its own text

/* (a) → 1 · (1) → 2 · (A) → 3, exactly the three levels the mockup defines. A multi-character
   lowercase marker is a roman numeral — (i), (ii) — which sits below (A); with three levels drawn
   it renders at the deepest rather than climbing back to the top. */
function levelOf(inner) {
  if (/^\d+$/.test(inner)) return 2;
  if (/^[A-Z]+$/.test(inner)) return 3;
  return inner.length > 1 ? 3 : 1;
}

function statuteLines(text) {
  const raw = String(text || "").split(/\n/).map((l) => l.trim()).filter(Boolean);
  const out = [];
  let level = 1;
  for (let i = 0; i < raw.length; i++) {
    const line = raw[i];
    const bare = line.match(MARKER);
    if (bare) {
      level = levelOf(bare[1]);
      // The clause this marker introduces is the next line. A marker with nothing after it is
      // still emitted, alone, rather than swallowed.
      const next = raw[i + 1];
      if (next && !MARKER.test(next)) { out.push({ key: out.length, level, marker: line, text: next }); i++; }
      else out.push({ key: out.length, level, marker: line, text: "" });
      continue;
    }
    const inline = line.match(INLINE);
    if (inline) {
      level = levelOf(inline[2]);
      out.push({ key: out.length, level, marker: inline[1], text: line.slice(inline[0].length) });
      continue;
    }
    // A continuation — a flush paragraph under the clause it belongs to. It keeps that clause's
    // level so the text stays aligned under itself rather than jumping to the margin.
    out.push({ key: out.length, level, marker: null, text: line });
  }
  return out;
}

const SKELETON = ["w95", "w88", "w95", "w74", "w40", "w88", "w95", "w60"];

/**
 * ONE authority, read in place.
 *
 * @param {object|null} authority  the rail row — { id, caseId, kind, type, name, cite, … }
 * @param {Function}    onClose    dismiss. Esc and backdrop click both route here via `Modal`.
 */
export default function AuthorityModal({ authority, onClose }) {
  const open = Boolean(authority);
  const statute = isStatute(authority);

  const [body, setBody] = useState(null);
  const [failed, setFailed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  /* 🔴 `seq` AND THE ABORT ARE BOTH LOAD-BEARING, AND RUN AH MEASURED WHY. Guarding the setState is
     enough for CORRECTNESS — without it a slow first response overwrites a fast second one and the
     reader clicks B and sees A. It is NOT enough for LATENCY: a browser opens at most six
     connections per origin over HTTP/1.1, so abandoned opinion fetches queue the one the reader is
     actually waiting for behind rows they have already left. One controller per open, aborted on
     the next. */
  const seq = useRef(0);
  const inflight = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const mine = ++seq.current;
    try { inflight.current?.abort(); } catch { /* an already-settled controller */ }
    const ac = new AbortController();
    inflight.current = ac;

    setBody(null);
    setFailed(null);
    setCopied(false);
    setSaved(false);

    const url = statute ? STATUTE_ROUTE(authority.cite) : (authority.caseId != null ? CASE_ROUTE(authority.caseId) : null);
    /* No cluster id and not a statute means there is nothing honest to fetch. The head still
       paints every fact the rail card holds and the body says plainly why it is empty — the same
       rule the card itself follows with "Not linked to an opinion record". */
    if (!url) { setFailed("unlinked"); return () => { try { ac.abort(); } catch { /* settled */ } }; }

    fetch(url, { signal: ac.signal, credentials: "same-origin" })
      /* 🔴 `res.ok` IS CHECKED BEFORE `.json()`. A5 shipped a fallback that parsed the ERROR BODY
         and stored an object with no results, so a failed fetch rendered as an empty success. A
         401 here is the ordinary shape when the session has lapsed and it must not read as "this
         opinion is blank". */
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (mine !== seq.current) return;
        if (statute) {
          const fed = d?.federal?.found ? d.federal : null;
          /* The state lane returns rows rather than a `federal` block. Either can carry text;
             neither is invented if both are absent. */
          const row = fed || (Array.isArray(d?.results) ? d.results.find((r) => r?.text) : null) || null;
          if (!row?.text) { setFailed("empty"); return; }
          setBody({
            kind: "statute",
            text: row.text,
            heading: row.heading || null,
            cite: row.cite || row.citation || null,
            /* ⭐ PROVENANCE. Rendered ONLY from stored columns, and only when they are actually
               present — an undated statute prints no currency line rather than a guessed one.
               `sourceRelease` is the public law number; `asOfDate` is `as_of_date`. */
            currency: row.currency?.asOfDate
              ? { asOf: row.currency.asOfDate, release: row.currency.sourceRelease || null }
              : (row.snapshotDate ? { asOf: row.snapshotDate, release: null } : null),
            source: row.currency ? "the U.S. Code" : "the official source",
          });
        } else {
          const text = d?.opinion?.text;
          if (!text) { setFailed("empty"); return; }
          setBody({
            kind: "case",
            text,
            court: d.court || null,
            dateFiled: d.dateFiled || null,
            citation: d.citation || null,
            /* 🔴 TRUNCATION IS DISCLOSED, NEVER PRESENTED AS COMPLETENESS. This repo has paid for
               that twice — a `slice(0,400)` that decided an answer, a cite-only dedupe that
               destroyed law. `/reading` says so in the payload (`opinion.truncated`), and if this
               surface ever asks for a `preview` the reader is told. */
            truncated: Boolean(d.opinion.truncated),
            preview: d.opinion.preview || null,
          });
        }
      })
      .catch((e) => {
        if (e?.name === "AbortError" || mine !== seq.current) return;
        setFailed(e.message || "failed");
      });

    return () => { try { ac.abort(); } catch { /* settled */ } };
  }, [open, statute, authority?.caseId, authority?.cite]);

  const loaded = Boolean(body);
  const href = authority ? researchHref(authority) : null;
  /* The fetched citation is the CANONICAL one (steel's `caseCitation`, built from the cluster's own
     reporter rows); the rail's `cite` is what the answer envelope carried. Prefer the canonical one
     once it exists, and fall back rather than offering nothing. */
  const citation = body?.citation || body?.cite || authority?.cite || null;

  const copy = useCallback(() => {
    if (!citation) return;
    navigator.clipboard?.writeText(citation).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
      () => { /* a denied clipboard permission is not an error worth a dialog */ },
    );
  }, [citation]);

  /* Save writes the SAME row Research's reading pane writes (`POST /api/library`, keyed on
     caseId). 🔴 It is case-only because the table is: `steel_library` is keyed on `case_id` and a
     statute has no cluster. So the control is disabled for a statute WITH ITS REASON rather than
     silently doing nothing — a control that lies about having acted is worse than no control, and
     this family has already shipped one that spent three bundles inert. */
  const save = useCallback(() => {
    if (!authority?.caseId) return;
    fetch("/research/api/library", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        caseId: authority.caseId,
        caseName: authority.name || null,
        cite: citation,
        court: body?.court || null,
      }),
    })
      .then((r) => { if (r.ok) setSaved(true); })
      .catch(() => { /* reported by the control staying un-ticked, not by a dialog over the text */ });
  }, [authority?.caseId, authority?.name, citation, body?.court]);

  if (!open) return null;

  const kindLabel = statute ? "Statute" : (authority.type || "Case");
  const year = authority?.year ?? null;
  /* 🔴 RUN UC · STEP 3 — THE FALLBACK READ FOR `court`. FLAGGED: this is RB's file and RUN UC's
     brief allowed exactly one edit in it — "verify the header consumes `court`, edit it only if the
     fallback read is missing, and say which." IT WAS MISSING. `year` had one (the line above);
     `court` was read from `body` alone, so the header waited on the fetch for a field the rail can
     now hand it at 0 ms. This is that one read, and it is the only change to this file.

     Precedence mirrors `dateLine` below: the BODY wins when it lands (it is the case's own record,
     fetched fresh), the rail value paints until then. On a turn answered before RUN UC — and on any
     statute — `authority.court` is null and the row behaves exactly as it does today. */
  const court = body?.court ?? authority?.court ?? null;
  const dateLine = body?.dateFiled
    ? `Decided ${new Date(body.dateFiled).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })}`
    : (year ? String(year) : null);

  return (
    <Modal open={open} onClose={onClose}>
      <div className="cc-authmodal" data-kind={statute ? "statute" : "case"} data-testid="clark-authority-modal">

        {/* 🔴 PINNED TO THE MODAL'S TRUE TOP-RIGHT CORNER, NOT INLINE BESIDE THE TITLE. Adam's
            ruling, 2026-08-24. It is positioned against `.cc-modal-card` — which the primitive
            already sets `position:relative` — so it holds when a long case name wraps to two
            lines, and `.cc-authmodal-headtop` reserves 44px for it. */}
        <button className="cc-authmodal-x" aria-label="Close" onClick={onClose} data-testid="clark-authority-modal-close">
          <svg className="cc-authmodal-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>

        {/* ══ THE HEAD — every element below paints at 0 ms from the rail card. No fetch. ═══════ */}
        <div className="cc-authmodal-head">
          <div className="cc-authmodal-headtop">
            <div>
              <p className="cc-authmodal-kind">{kindLabel}</p>
              <h2 className={`cc-authmodal-title${statute ? " is-statute" : ""}`} data-testid="clark-authority-modal-title">
                {authority.name || authority.cite || "—"}
              </h2>
              {/* The section's own name — a heading, not a status. Arrives with the body. */}
              {statute && body?.heading && <p className="cc-authmodal-subtitle">{body.heading}</p>}
            </div>
          </div>

          {/* ⚠️ THE META ROW FILLS IN, AND THAT IS DESIGNED RATHER THAN TOLERATED. `cite` and — since
              RUN UC — `court` are present at 0 ms; only the DATE arrives with the body, and it
              REPLACES the year rather than extending the row. The row is laid out so a late arrival
              extends it instead of moving the title above it.
              🔴 An answer given before RUN UC restores with `court: null` (nothing backfills), so
              the pre-UC behaviour — court arriving with the body — is still what those turns do. */}
          <div className="cc-authmodal-meta">
            {authority.name && authority.cite && authority.cite !== "—" && (
              <span className="cc-authmodal-r">{authority.cite}</span>
            )}
            {court && <><i>·</i><span>{court}</span></>}
            {dateLine && <><i>·</i><span>{dateLine}</span></>}
          </div>

          {/* ⭐ PROVENANCE — STATUTES ONLY. NOT TREATMENT. DO NOT DELETE.
              The cardinal rule made visible: statute text comes from govinfo/GPO and eCFR, never
              from a model. Rendered only from stored currency columns; absent when they are. */}
          {statute && body?.currency && (
            <p className="cc-authmodal-prov" data-testid="clark-authority-modal-provenance">
              Text as of <b>{body.currency.asOf}</b>
              {body.currency.release ? ` (${body.currency.release})` : ""} — verbatim from {body.source}.
              Verify no later amendment.
            </p>
          )}

          {/* 🔴 JP'S RULE — ONE FILLED BUTTON CARRIES THE PRIME VOICE ALONE.
              The secondaries are disabled until the body loads: they currently render before the
              content they act on exists, which is its own defect. */}
          <div className="cc-authmodal-acts">
            <button className="cc-authmodal-btn" onClick={copy} disabled={!loaded || !citation}>
              <svg className="cc-authmodal-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 9h10v10H9zM5 15V5h10" /></svg>
              {copied ? "Copied" : "Copy citation"}
            </button>
            <button
              className="cc-authmodal-btn"
              onClick={save}
              disabled={!loaded || !authority.caseId || saved}
              title={authority.caseId ? undefined : "Saving is keyed to an opinion record; this authority has none."}
            >
              <svg className="cc-authmodal-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4l2.4 5 5.6.8-4 3.9 1 5.5-5-2.7-5 2.7 1-5.5-4-3.9 5.6-.8z" /></svg>
              {saved ? "Saved" : "Save"}
            </button>
            {/* ⭐ RUN MA — THE HOLD IS OVER, AND THE HELD TITLE GOES WITH IT.
                Adam ruled on 2026-08-25 what an authority-on-a-matter IS: *"authorities found in
                Clark or CiteClerk Research can be tied to a matter … It has nothing to do with
                actual answers."* That is a durable fact about a matter, so it got a table
                (`matter_authorities`, steel migration 019) rather than a third `steel_history`
                kind — RUN LF measured that `steel_project_items` is a pure POINTER into the
                history log, and you cannot say "this case belongs to this matter" through a
                pointer to an event without inventing an event that never happened.

                🔴 THE OLD `title` IS DELETED, NOT EDITED, AND THAT IS THE POINT. It read *"Held:
                it has not been decided where an authority attached to a matter is stored…"* — true
                when RUN LH wrote it, false the moment 019 landed. ⭐ A stale honest sentence is
                worse than a missing one, because it was believed: a lawyer who read it would go
                on believing the feature was unbuilt while the button beside it worked.

                ⚠️ AND THE COPY CLAIMS ONLY WHAT IT DOES. Filing ties the CASE to the MATTER. It
                attaches no text, ratifies nothing, and does not put the case in a draft —
                ratification is the lawyer's act and it is the moat. The control's own menu says so
                at the moment of the decision; see FileToMatter.jsx.

                `disabled` still tracks the body load, exactly as the two controls above it do, so
                nothing about WHEN the row is actionable changes. */}
            <FileToMatter
              /* ⚠️ THE LIVE main→steel PATH, AND IT NEEDS NO NEW PROXY. Caddy forwards
                 /research/api/* to the research app, which strips the prefix BEFORE the /api auth
                 middleware — so Clerk applies normally (RUN LF traced every hop). This is the same
                 route shape `Save` above already uses.
                 🔴 IT IS DEAD IN LOCAL DEV: apps/main/vite.config.mjs proxies /api and /cite, NOT
                 /research. A dev-mode click fails for a reason that has nothing to do with this
                 feature. Flagged in ops/run-ma/RUN-MA-filing.md. */
              apiBase="/research"
              clusterId={authority.caseId ?? null}
              caseName={authority.name || null}
              /* The canonical citation once the body lands, the rail's until then — the same
                 precedence the meta row above uses, so the filed row cannot disagree with the
                 screen it was filed from. */
              cite={citation}
              court={court}
              year={year}
              /* ⚠️ NULL, AND CORRECTLY SO. RUN TD removed treatment from this rail entirely on
                 Adam's instruction, so Clark HOLDS no citator verdict to file. The column stores
                 the verdict AS HELD and never invents an 'unknown'. */
              goodLaw={null}
              buttonClassName="cc-authmodal-btn"
              label="Save to a Matter"
              disabled={!loaded}
            />
            <span className="cc-authmodal-sp" />
            {href && (
              <a className="cc-authmodal-btn is-prime" href={href} data-testid="clark-authority-modal-research">
                Open in Research
                <svg className="cc-authmodal-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </a>
            )}
          </div>
        </div>

        {/* ══ THE BODY — the only thing that scrolls ══════════════════════════════════════════ */}
        <div className="cc-authmodal-body" data-testid="clark-authority-modal-body">
          <p className="cc-authmodal-bodyhead">{statute ? "Text" : "Opinion"}</p>

          {failed ? (
            /* An honest terminal state. It says which of the two things happened — we hold no
               record, or the request failed — because those are different and a lawyer acts
               differently on each. */
            <p className="cc-authmodal-note" data-testid="clark-authority-modal-failed">
              {failed === "unlinked"
                ? "This authority is not linked to an opinion record, so there is no text to show."
                : failed === "empty"
                  ? "We do not hold the text for this authority."
                  : "The text could not be loaded just now. Open in Research to try the full reader."}
            </p>
          ) : !loaded ? (
            /* ⚠️ SKELETON, NOT A SPINNER. At eight seconds a spinner reads as failure; lines in the
               shape of the content read as arriving. */
            <div data-testid="clark-authority-modal-skeleton">
              <p className="cc-authmodal-loadnote">
                <span className="cc-authmodal-dot" />
                {statute ? "Retrieving the text from the official source…" : "Retrieving the opinion from the corpus…"}
              </p>
              {SKELETON.map((w, i) => <span key={i} className={`cc-authmodal-sk is-${w}`} />)}
            </div>
          ) : statute ? (
            <div className="cc-authmodal-stat">
              {statuteLines(body.text).map((l) => (
                <p key={l.key} className={`is-lv${l.level}`}>
                  {l.marker && <b>{l.marker}</b>}{l.marker ? " " : ""}{l.text}
                </p>
              ))}
            </div>
          ) : (
            <div className="cc-authmodal-opinion">
              {String(body.text).split(/\n\n+/).filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
              {/* 🔴 A PREVIEW SAYS IT IS ONE. */}
              {body.truncated && (
                <p className="cc-authmodal-note">
                  Showing the first {body.preview?.blocks} of {body.preview?.ofBlocks} paragraphs. Open in Research for the full opinion.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="cc-authmodal-foot">
          <span>
            {statute
              ? "Verbatim from the official source. Currency is disclosed, never guessed."
              : "Verbatim from the corpus. Not a substitute for reading the source."}
          </span>
          <span className="cc-authmodal-sp" />
          <span><kbd>Esc</kbd> to close</span>
        </div>
      </div>
    </Modal>
  );
}
